"use client"

import * as React from "react"
import { Users, Plus, Trash2, ShieldCheck, AlertTriangle } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

interface WorkspaceMembersManagerProps {
  workspaceId: string
  workspaceName: string
  allUsers: any[]
}

export function WorkspaceMembersManager({
  workspaceId,
  workspaceName,
  allUsers,
}: WorkspaceMembersManagerProps) {
  const [members, setMembers] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [userToRemove, setUserToRemove] = React.useState<{ userId: string; name: string } | null>(null)
  const [isRemoving, setIsRemoving] = React.useState(false)

  const fetchMembers = React.useCallback(async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    setLoading(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetWorkspaceMembers($workspaceId: ID!) {
              workspaceMembers(workspaceId: $workspaceId) {
                id
                userId
                role
                user {
                  id
                  name
                  email
                  avatarUrl
                }
              }
            }
          `,
          variables: { workspaceId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.workspaceMembers) {
        setMembers(result.data.workspaceMembers)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  React.useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const handleAddMember = async (userId: string, role = "member") => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation AddWorkspaceMember($workspaceId: ID!, $userId: ID!, $role: String) {
              addWorkspaceMember(workspaceId: $workspaceId, userId: $userId, role: $role)
            }
          `,
          variables: { workspaceId, userId, role }
        })
      })
      const result = await res.json()
      if (result.data && result.data.addWorkspaceMember) {
        toast.success("Membro adicionado ao workspace!")
        fetchMembers()
      } else {
        toast.error("Erro ao adicionar membro")
      }
    } catch (e) {
      toast.error("Erro de conexão")
    }
  }

  const handleUpdateRole = async (userId: string, newRole: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation UpdateWorkspaceMemberRole($workspaceId: ID!, $userId: ID!, $role: String!) {
              updateWorkspaceMemberRole(workspaceId: $workspaceId, userId: $userId, role: $role)
            }
          `,
          variables: { workspaceId, userId, role: newRole }
        })
      })
      const result = await res.json()
      if (result.data && result.data.updateWorkspaceMemberRole) {
        toast.success("Papel no workspace atualizado!")
        setMembers(prev =>
          prev.map(m => (m.userId === userId ? { ...m, role: newRole } : m))
        )
      } else if (result.errors && result.errors[0]) {
        toast.error(result.errors[0].message)
      }
    } catch (e) {
      toast.error("Erro ao atualizar papel")
    }
  }

  const handleRemoveMember = async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation RemoveWorkspaceMember($workspaceId: ID!, $userId: ID!) {
              removeWorkspaceMember(workspaceId: $workspaceId, userId: $userId)
            }
          `,
          variables: { workspaceId, userId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.removeWorkspaceMember) {
        toast.success("Membro removido do workspace!")
        setMembers(prev => prev.filter(m => m.userId !== userId))
      } else if (result.errors && result.errors[0]) {
        toast.error(result.errors[0].message)
      }
    } catch (e) {
      toast.error("Erro ao remover membro")
    }
  }

  // Identificar usuários que são Administradores ou Proprietários da EMPRESA
  const companyAdminsAndOwners = React.useMemo(() => {
    return (allUsers || []).filter(u => u.role === "owner" || u.role === "admin")
  }, [allUsers])

  const companyAdminUserIds = React.useMemo(() => {
    const set = new Set<string>()
    companyAdminsAndOwners.forEach(u => {
      if (u.id) set.add(String(u.id))
      if (u.userId) set.add(String(u.userId))
    })
    return set
  }, [companyAdminsAndOwners])

  const companyAdminEmails = React.useMemo(() => {
    const set = new Set<string>()
    companyAdminsAndOwners.forEach(u => {
      if (u.email) set.add(String(u.email).toLowerCase())
      if (u.user?.email) set.add(String(u.user.email).toLowerCase())
    })
    return set
  }, [companyAdminsAndOwners])

  // Lista consolidada de membros garantindo inclusão dos admins/owners da empresa
  const displayMembers = React.useMemo(() => {
    const list = [...members]
    companyAdminsAndOwners.forEach(adminUser => {
      const uid = String(adminUser.userId || adminUser.id || '')
      const email = String(adminUser.email || adminUser.user?.email || '').toLowerCase()

      const alreadyInList = list.some(m => {
        const mUid = String(m.userId || m.id || '')
        const mEmail = String(m.user?.email || m.email || '').toLowerCase()
        return (uid && mUid === uid) || (email && mEmail === email)
      })

      if (!alreadyInList) {
        list.unshift({
          id: `company-admin-${uid}`,
          userId: uid,
          role: "admin",
          isCompanyAdminOrOwner: true,
          user: {
            id: uid,
            name: adminUser.name || adminUser.user?.name,
            email: adminUser.email || adminUser.user?.email,
            avatarUrl: adminUser.avatarUrl || adminUser.user?.avatarUrl,
            role: adminUser.role
          }
        })
      }
    })
    return list
  }, [members, companyAdminsAndOwners])

  const assignedUserIds = displayMembers.map(m => String(m.userId))
  const availableUsers = (allUsers || []).filter(u => {
    const uid = String(u.id || u.userId || '')
    const uEmail = String(u.email || u.user?.email || '').toLowerCase()
    const isCompanyAdmin = companyAdminUserIds.has(uid) || (uEmail !== '' && companyAdminEmails.has(uEmail))
    return !isCompanyAdmin && !assignedUserIds.includes(uid)
  })

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200/60 dark:border-zinc-800/60 space-y-3 font-sans">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-bold text-zinc-700 dark:text-zinc-300">
          <Users className="size-4 text-[#004EB1]" />
          <span>Membros do Workspace ({displayMembers.length})</span>
        </div>

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger className="inline-flex items-center justify-center rounded-md text-xs font-semibold h-7 px-3 border border-dashed border-zinc-300 dark:border-zinc-700 bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-800 dark:text-zinc-200 gap-1 cursor-pointer transition-colors">
            <Plus className="size-3.5 text-[#004EB1]" />
            Adicionar Usuário
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl">
            <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-2 px-1">
              Adicionar ao Workspace
            </p>
            {availableUsers.length === 0 ? (
              <p className="text-xs text-zinc-400 italic p-2">Todos os usuários já pertencem a este workspace.</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                {availableUsers.map(u => (
                  <button
                    key={u.id || u.userId}
                    type="button"
                    onClick={() => {
                      handleAddMember(u.id || u.userId, "member")
                      setIsOpen(false)
                    }}
                    className="w-full text-left p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-between text-xs cursor-pointer"
                  >
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200 truncate">{u.name || u.email}</span>
                    <Plus className="size-3.5 text-blue-600 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {displayMembers.length === 0 ? (
        <p className="text-xs text-zinc-400 dark:text-zinc-500 italic py-1">
          Nenhum membro configurado especificamente.
        </p>
      ) : (
        <div className="space-y-2">
          {displayMembers.map(m => {
            const companyUser = (allUsers || []).find(u => {
              const uid = String(u.id || u.userId || '')
              const mUid = String(m.userId || '')
              const uEmail = String(u.email || u.user?.email || '').toLowerCase()
              const mEmail = String(m.user?.email || m.email || '').toLowerCase()
              return (uid !== '' && uid === mUid) || (uEmail !== '' && uEmail === mEmail)
            })

            const usr = companyUser || m.user || m

            const memberUserId = String(m.userId || usr?.id || usr?.userId || '')
            const memberEmail = String(usr?.email || m.user?.email || m.email || '').toLowerCase()

            const isCompanyAdminOrOwner =
              companyAdminUserIds.has(memberUserId) ||
              (memberEmail !== '' && companyAdminEmails.has(memberEmail)) ||
              companyUser?.role === "owner" ||
              companyUser?.role === "admin" ||
              usr?.role === "owner" ||
              usr?.role === "admin" ||
              Boolean(m.isCompanyAdminOrOwner)

            return (
              <div
                key={m.id}
                className="flex items-center justify-between p-2 rounded-lg bg-zinc-50 dark:bg-zinc-850/60 border border-zinc-200/50 dark:border-zinc-800/50"
              >
                <div className="flex items-center gap-2.5">
                  <div className="size-6 rounded-full bg-blue-500/10 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 flex items-center justify-center font-bold text-[10px]">
                    {(usr?.name || usr?.email || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-900 dark:text-white leading-tight">
                      {usr?.name || "Usuário"}
                    </p>
                    <p className="text-[10px] text-zinc-400">{usr?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isCompanyAdminOrOwner ? (
                    <span
                      className="text-[10px] font-bold text-[#004EB1] dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/40 px-2 py-0.5 rounded-md flex items-center gap-1 select-none"
                      title="Administrador de todos os Workspaces da empresa"
                    >
                      <ShieldCheck className="size-3 text-[#004EB1] dark:text-blue-400" />
                      Admin da Empresa
                    </span>
                  ) : (
                    <>
                      <select
                        value={m.role || "member"}
                        onChange={e => handleUpdateRole(m.userId, e.target.value)}
                        className="text-[11px] font-semibold bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md px-2 py-0.5 text-zinc-700 dark:text-zinc-200 cursor-pointer"
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Membro</option>
                      </select>

                      <button
                        type="button"
                        onClick={() => setUserToRemove({ userId: m.userId, name: usr?.name || usr?.email || "Usuário" })}
                        className="text-red-500 hover:text-red-600 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-955/30 transition-colors cursor-pointer"
                        title="Remover membro do workspace"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal de Confirmação de Remoção de Membro */}
      {userToRemove && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl p-5 space-y-4 animate-in zoom-in-95 duration-150 font-sans">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <AlertTriangle className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Remover do Workspace</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{workspaceName || "Workspace"}</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
              Tem certeza que deseja remover <strong className="text-zinc-900 dark:text-white font-semibold">{userToRemove.name}</strong> deste workspace? O usuário perderá o acesso a este espaço de trabalho.
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setUserToRemove(null)}
                disabled={isRemoving}
                className="text-xs h-8 px-3 cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isRemoving}
                onClick={async () => {
                  setIsRemoving(true)
                  try {
                    await handleRemoveMember(userToRemove.userId)
                    setUserToRemove(null)
                  } finally {
                    setIsRemoving(false)
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8 px-4 cursor-pointer"
              >
                {isRemoving ? "Removendo..." : "Confirmar Remoção"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
